import { TUser } from "@/types/types";

// Define the profile steps interface
export interface ProfileStep {
  id: number;
  label: string;
  fields: (keyof TUser)[];
  weight: number;
}

export const profileSteps: ProfileStep[] = [
  {
    id: 1,
    label: "Personal Details",
    fields: [
      "fullName",
      "phoneNumber",
      "age",
      "gender",
      "address",
      "bio",
      "skinTone",
      "bodyType",
    ],
    weight: 20,
  },
  {
    id: 2,
    label: "Professional Details",
    fields: [
      "active",
      "socialMedia",
      "experience",
      "hoursPerWeek",
      "availableInWeekends",
      "salary",
      "payPerVideo",
      "wardrobeStyles",
      "availableBackgrounds",
      "language",
    ],
    weight: 50,
  },
  {
    id: 3,
    label: "Project Setup Details",
    fields: [
      "hasRelativesInVideo",
      "props",
      "greenScreen",
      "noiselessRoomAndMicrophoneSpecs",
      "cameraOrSmartphoneSpecs",
      "lightingSpecs",
      "additionalInfo",
    ],
    weight: 30,
  },
];

// Utility function to calculate step completion
export const calculateStepCompletion = (data: TUser, step: ProfileStep) => {
  const completedFields = step.fields.filter((field) => {
    if (!data) return false;

    const fieldValue = data[field];

    // Special handling for address object
    if (field === "address" && typeof fieldValue === "object") {
      const addressObj = fieldValue as TUser["address"];
      return Boolean(
        addressObj?.country ||
        addressObj?.city ||
        addressObj?.state ||
        addressObj?.zipCode
      );
    }

    // Handle arrays
    if (Array.isArray(fieldValue)) {
      return fieldValue.length > 0;
    }

    // Handle boolean values
    if (typeof fieldValue === "boolean") {
      return true;
    }

    // Handle regular fields
    return (
      fieldValue !== undefined && fieldValue !== null && fieldValue !== ""
    );
  });

  return {
    completedCount: completedFields.length,
    totalCount: step.fields.length,
    percentage: Math.round(
      (completedFields.length / step.fields.length) * 100
    ),
    weight: step.weight,
  };
};

// Hook to calculate profile completion
export const useProfileCompletion = (data: TUser) => {
  // Calculate step completions
  const stepCompletions = profileSteps.map((step) => 
    calculateStepCompletion(data, step)
  );

  // Calculate overall profile completion
  const totalProfileCompletion = stepCompletions.reduce(
    (total, step) => total + (step.percentage * step.weight) / 100,
    0
  );

  return {
    totalProfileCompletion: Math.round(totalProfileCompletion),
    stepCompletions,
    isProfileComplete: totalProfileCompletion === 100,
  };
};